import Button from "@/common/components/Button";
import { FileUploader } from "@/common/components/FileUploader";
import Modal from "@/common/components/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { useCreateBillPayment } from "../../hooks/useCreatePayment";
import { addPaymentSchema, AddPaymentValidationSchema } from "../../schemas/addPayment.schema";
import SelectController from "@/common/components/SelectController";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { billPaymentOptions } from "../../constants/billPaymentType.constant";

interface AddPaymentModalProps {
  billId: string;
}

const AddPaymentModal = ({ billId }: AddPaymentModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [pdfFile, setPdfFile] = useState<File>();

  const { mutate: createBillPayment } = useCreateBillPayment({
    billId,
    onSuccess() {
      resetForm();
      setIsOpen(false);
      setIsError(false);
      setMessages(['Cobrança adicionada com sucesso!']);
    },
    onError(error) {
      FormTransformers.errorsTransformer({ setError })(error);

      const { errors } = error?.response?.data as { errors: Record<string, string[]> };

      if (errors.base) {
        setIsError(true);
        setMessages(errors.base);
      }
    },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<AddPaymentValidationSchema>({
    resolver: zodResolver(addPaymentSchema),
    defaultValues: {
      paid_total: '',
    }
  });

  const onSubmit: SubmitHandler<AddPaymentValidationSchema> = (form) => createBillPayment({
    form,
    pdf: pdfFile,
  });

  const resetForm = () => {
    reset();
    setPdfFile(undefined);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          if (!value) {
            resetForm();
          }

          setIsOpen(value);
        }}
        Trigger={(
          <Button>
            Adicionar cobrança
          </Button>
        )}
        title="Adicionar cobrança"
        footer="Cadastrar cobrança"
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form className="flex flex-col gap-4">
            <SelectController
              control={control}
              name="type"
              label="Tipo de pagamento"
              markAsRequired
              options={billPaymentOptions}
              errorMessage={errors.type?.message || undefined}
            />

            <CurrencyTextField
              label="Valor total"
              markAsRequired
              {...register('paid_total')}
              errorMessage={errors.paid_total?.message || undefined}
            />

            <div className="flex flex-col gap-1">
              <FileUploader
                id={`fiscal-document-pdf-file-${useId()}`}
                title="Anexe o PDF da nota fiscal"
                className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                accept="application/pdf"
                fileName={pdfFile?.name}
                currentFileUrl={pdfFile ? URL.createObjectURL(pdfFile) : undefined}
                handleChange={setPdfFile}
              />

              {errors.pdf?.message && <ErrorMessage name='pdf' message={errors.pdf?.message} />}
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['billingBillPayments', billId] });
          }}
        />
      )}
    </>
  );
};

export default AddPaymentModal;
